import type { KeyBinding } from './types';
import type { CommandContext } from '@/commands/types';
import { commandRegistry } from '@/commands/registry';
import { isModKey } from '@/utils/platform';

export class KeybindingManager {
  private bindings: KeyBinding[];
  private getContext: () => CommandContext;
  private getMode: () => string;

  constructor(
    bindings: KeyBinding[],
    getContext: () => CommandContext,
    getMode: () => string,
  ) {
    this.bindings = bindings;
    this.getContext = getContext;
    this.getMode = getMode;
  }

  handleKeyDown = (e: KeyboardEvent): void => {
    // Skip keybindings when focus is in a form element (input, textarea, select, contenteditable)
    const target = e.target as HTMLElement | null;
    if (target) {
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
        // Still allow Escape to close panels/dialogs
        if (e.key !== 'Escape') return;
      }
    }

    const mode = this.getMode();

    for (const binding of this.bindings) {
      if (binding.normalModeOnly && mode === 'editing') continue;

      if (!this.matchesKey(e, binding)) continue;

      const ctx = this.getContext();
      const executed = commandRegistry.execute(binding.commandId, ctx);
      if (executed) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    }
  };

  private matchesKey(e: KeyboardEvent, binding: KeyBinding): boolean {
    if (!e.key) return false;
    if (e.key !== binding.key && e.key.toLowerCase() !== binding.key.toLowerCase()) {
      return false;
    }
    if (binding.ctrl && !isModKey(e)) return false;
    if (!binding.ctrl && isModKey(e)) return false;
    if (binding.shift && !e.shiftKey) return false;
    if (!binding.shift && e.shiftKey) return false;
    if (binding.alt && !e.altKey) return false;
    if (!binding.alt && e.altKey) return false;
    return true;
  }

  attach(element: HTMLElement | Window = window): () => void {
    element.addEventListener('keydown', this.handleKeyDown as EventListener);
    return () => {
      element.removeEventListener('keydown', this.handleKeyDown as EventListener);
    };
  }
}
